/**
 * Commercial terms: what a client pays (per inspection or a monthly plan) and how they pay.
 * Stored as zData rows in the account property's "Commercial" area. zInspector's public API
 * can't write zData yet: against the live API the account page previews the request below;
 * the demo's fake zInspector runs it (zcrm-docs/reference/proposed-api-writes.md, route 4).
 */
import {
  COMMERCIAL_AREA,
  COMMERCIAL_TERMS,
  PRICING_MODELS,
  type CommercialTermKey,
} from "./crmConfig";
import type { ZiWriteRequest } from "./newClient";

export type CommercialTerms = Record<CommercialTermKey, string>;

export const EMPTY_COMMERCIAL_TERMS = Object.fromEntries(
  COMMERCIAL_TERMS.map(({ key }) => [key, ""]),
) as CommercialTerms;

const clean = (value: unknown): string =>
  value == null ? "" : String(value).trim();

/**
 * The request that saves the terms that changed. A cleared field is sent with an empty value
 * so zInspector removes it. Null when nothing changed.
 */
export const buildCommercialTermsRequest = (
  propertyId: number | string,
  values: Partial<Record<CommercialTermKey, unknown>>,
  previous: Partial<CommercialTerms> = {},
): ZiWriteRequest | null => {
  const changed = COMMERCIAL_TERMS.filter(
    ({ key }) => key in values && clean(values[key]) !== clean(previous[key]),
  ).map(({ key, detail }) => ({ detail, value: clean(values[key]) }));
  if (changed.length === 0) return null;
  return {
    method: "POST",
    path: "/zdata/save/",
    body: { Property: propertyId, area_name: COMMERCIAL_AREA, values: changed },
  };
};

const money = (value: string) => {
  const amount = Number(value.replace(/[$,\s]/g, ""));
  return value && Number.isFinite(amount)
    ? `$${amount.toLocaleString("en-US")}`
    : value;
};

/** One line for the account header: "MIMO $139 · Periodic $109" or "Plan Pro · $1,200/mo". */
export const summarizeCommercialTerms = (
  terms: Partial<CommercialTerms>,
): string => {
  if (terms.pricing_model === PRICING_MODELS.plan) {
    const fee = terms.plan_monthly_fee
      ? `${money(terms.plan_monthly_fee)}/mo`
      : "";
    return [terms.plan_name ? `Plan ${terms.plan_name}` : "Plan", fee]
      .filter(Boolean)
      .join(" · ");
  }
  return [
    terms.price_mimo ? `MIMO ${money(terms.price_mimo)}` : "",
    terms.price_periodic ? `Periodic ${money(terms.price_periodic)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
};

/** Form validation: amounts and counts must be numbers when they are filled in. */
const MONEY_FIELDS = [
  "price_mimo",
  "price_periodic",
  "plan_monthly_fee",
  "plan_extra_price",
] as const;
const WHOLE_NUMBER_FIELDS = ["plan_included", "payment_days"] as const;

const isAmount = (value: string) => /^\$?\s*\d+(\.\d{1,2})?$/.test(value);
const isWholeNumber = (value: string) => /^\d+$/.test(value);

export const validateCommercialTerms = (
  values: Record<string, unknown>,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const text = (key: string) => String(values[key] ?? "").trim();
  for (const key of MONEY_FIELDS) {
    if (text(key) && !isAmount(text(key)))
      errors[key] = "Enter an amount, e.g. 139";
  }
  for (const key of WHOLE_NUMBER_FIELDS) {
    if (text(key) && !isWholeNumber(text(key)))
      errors[key] = "Enter a whole number";
  }
  return errors;
};
